import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, Select } from 'antd';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import useApi from 'vl-common/src/hooks/useApi';
import { CaseMeta } from 'vl-common/src/schemas/getMetaCase';
import useUser from '@vl-core/hooks/useUser';
import { ConfirmationModal, SuccessModal, confirmationModal } from 'vl-common/src/components/Modals';

const { Option } = Select;

const SelfDischarge = ({
  canDischarge,
  caseId,
  caseCondition
}: {
  canDischarge: boolean;
  caseId: number;
  caseCondition?: string;
}) => {
  const { user_guid } = useUser();
  const { Api } = useApi();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const [form] = Form.useForm();

  const [dischargeModalVisible, setDischargeModalVisible] = useState(false);
  const [caseDischargeReason, setCaseDischargeReason] = useState('');
  const [dischargeAlertMessage, setDischargeAlertMessage] = useState('');
  const [dischargeCompleted, setDischargeCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data: caseDischargeReasons = [] } = useQuery({
    queryKey: ['patientsCaseMetaData', caseId],
    queryFn: async () => {
      const caseMetadataList = await Api.meta.getUsersCaseMeta(caseId);

      const caseDischargeReasons: CaseMeta['case_discharge_reasons'] =
        caseMetadataList?.data.meta[0].case_discharge_reasons;

      return caseDischargeReasons.map((reason) => ({
        label: reason.discharge_desc,
        value: reason.discharge_code
      }));
    }
  });

  const caseDischargeReasonByDefault = useMemo(() => {
    return caseDischargeReasons[0]?.value;
  }, [caseDischargeReasons]);

  const isSuccessResponse = (res: any) => res.type === actionTypes.PATIENTS_DISCHARGE_SUCCESS && res.payload;
  const isFailResponse = (res: any) => res.type === actionTypes.PATIENTS_DISCHARGE_FAIL && res.error;

  const sendDischargeRequest = async (dry_run = false) => {
    setDischargeAlertMessage('');
    const request = {
      user_guid,
      case_id: caseId,
      appt_id: null,
      discharge_code: caseDischargeReason || caseDischargeReasonByDefault,
      dry_run
    };
    setIsLoading(true);
    const response = await dispatch(actions.patientsDischarge(request), false);
    setIsLoading(false);
    return response;
  };

  const dischargeDryRun = async () => {
    const response = await sendDischargeRequest(true);
    handleDischargeResponse(response, onDischargeSave, setDischargeAlertMessage, onDischargeWithOpenAppointments);
  };

  const onDischargeWithOpenAppointments = async () => {
    const response = await sendDischargeRequest();
    handleDischargeResponse(response, () => setDischargeCompleted(true), setDischargeAlertMessage);
  };

  const onCloseDischargeModal = () => {
    setDischargeModalVisible(false);
    setDischargeAlertMessage('');
    setCaseDischargeReason(caseDischargeReasonByDefault);
  };

  const handleDischargeResponse = (
    response: any,
    onSuccess: () => void,
    onError: (message: string) => void,
    onAppointmentHasOutstandingAppts = () => {}
  ) => {
    if (isSuccessResponse(response)) {
      onSuccess();
      return;
    }

    if (isFailResponse(response)) {
      const { status, code, statusText } = response.error;
      if (status === 412) {
        onError(t('DischargeWithOutstandingReferrals'));
        return;
      }

      if (code === 417) {
        setDischargeModalVisible(false);
        confirmationModal({
          title: t('DischargeWithOpenAppointmentsTitle'),
          content: t('DischargeWithOpenAppointmentsContent'),
          onOk: () => onAppointmentHasOutstandingAppts()
        });
        return;
      }

      if (status === 425) {
        onError(t('DischargeWithOutstandingInvestigationRequests'));
        return;
      }

      onError(statusText || t('UnableToDischargeCase'));
    }
  };

  const onDischargeSave = async () => {
    const response = await sendDischargeRequest();

    if (isSuccessResponse(response)) {
      setDischargeModalVisible(false);
      setDischargeCompleted(true);
    } else if (isFailResponse(response)) {
      setDischargeAlertMessage(response.error.message);
    }
  };

  return (
    <>
      <Button
        disabled={!canDischarge}
        type="primary"
        onClick={() => setDischargeModalVisible(true)}
        data-testid="discharge-button"
        style={{ marginRight: '20px' }}
      >
        {t('Discharge yourself')}
      </Button>

      <ConfirmationModal
        title="Discharge yourself"
        open={dischargeModalVisible}
        onCancel={onCloseDischargeModal}
        cancelButtonProps={{ 'data-testid': 'cancel-button' }}
        onOk={dischargeDryRun}
        okText="Confirm"
        okButtonProps={{ 'data-testid': 'confirm-discharge-button', loading: isLoading, disabled: isLoading }}
      >
        <Form form={form} layout="vertical">
          {!!dischargeAlertMessage && (
            <Alert type="error" css={{ margin: '8px 0px' }} showIcon message={dischargeAlertMessage} />
          )}
          <p className="description">
            {t('Please select a reason why you wish to discharge yourself')}
            {caseCondition ? (
              <span>
                &nbsp; from the following case: "<b>{caseCondition}</b>"
              </span>
            ) : (
              ''
            )}
          </p>
          <Form.Item>
            <Select
              placeholder={t('Select')}
              onChange={(value) => setCaseDischargeReason(value)}
              value={caseDischargeReason || caseDischargeReasonByDefault}
              data-testid="case-discharge-reason-select"
            >
              {caseDischargeReasons?.map(({ value, label }, index) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </ConfirmationModal>

      <SuccessModal
        open={dischargeCompleted}
        title="Discharge completed"
        onCancel={() => {
          setDischargeCompleted(false);
          window.location.reload();
        }}
      />
    </>
  );
};

export default SelfDischarge;
